import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { definirDoisFatoresPendente } from "@/api/space/estadoAutenticacao.space";
import useAutenticacaoJwt from "../../../../useAutenticacaoJwt";
import { lerGrupoLembradoLogin, removerGrupoLembradoLogin } from "../../../../utils/grupoLembradoLogin";
import type { FormularioLoginUnificado } from "../loginUnificado.schema";
import { construirLoginUnificadoRequest } from "../construirLoginUnificadoRequest";
import { obterContextoGrupoLogin } from "../utils/grupoLogin.utils";
import { obterMensagemErroLogin } from "../utils/loginUnificado.utils";
import type { DesafioOtpLogin } from "../loginUnificado.types";
import type { IdentificacaoGrupoLogin } from "../../../../tiposAutenticacaoJwt";
import useBloqueioTentativasLogin from "./useBloqueioTentativasLogin";

// ------------------------------- FUNCTIONS -------------------------------
export default function useFluxoLoginUnificado() {
	// -------------------------------- STATES --------------------------------
	const [grupoLembrado, setGrupoLembrado] = useState(lerGrupoLembradoLogin);
	const [desafio, setDesafio] = useState<DesafioOtpLogin | null>(null);
	const [entradaSpace, setEntradaSpace] = useState<IdentificacaoGrupoLogin | null>(null);
	const [mensagemErro, setMensagemErro] = useState<string | null>(null);
	const [enviando, setEnviando] = useState(false);
	const controllerRef = useRef<AbortController | null>(null);
	const montadoRef = useRef(false);
	const bloqueio = useBloqueioTentativasLogin();
	const { entrarUnificado } = useAutenticacaoJwt();
	const { slug } = useParams<{ slug: string }>();
	const navigate = useNavigate();

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		montadoRef.current = true;
		definirDoisFatoresPendente(false);
		return () => {
			montadoRef.current = false;
			controllerRef.current?.abort();
			definirDoisFatoresPendente(false);
		};
	}, []);

	// ----------------------------- DERIVED STATE ----------------------------
	const grupo = obterContextoGrupoLogin(slug, grupoLembrado);

	// -------------------------------- HANDLERS ------------------------------
	const cancelarDesafio = useCallback(() => {
		controllerRef.current?.abort();
		controllerRef.current = null;
		setDesafio(null);
		setEntradaSpace(null);
		definirDoisFatoresPendente(false);
	}, []);

	const abrirSpace = useCallback((identificacao: IdentificacaoGrupoLogin) => {
		setMensagemErro(null);
		setEntradaSpace(identificacao);
		definirDoisFatoresPendente(true);
	}, []);

	const expirarDesafio = useCallback(() => {
		cancelarDesafio();
		setMensagemErro("O desafio de verificação expirou. Inicie um novo login.");
	}, [cancelarDesafio]);

	async function entrar(dados: FormularioLoginUnificado, token: string): Promise<200 | 202 | null> {
		if (controllerRef.current || desafio || entradaSpace || bloqueio.estaBloqueado()) return null;

		const identificacao = grupo.identificacao ?? { tipo: "codigo", valor: dados.codigo };
		const controller = new AbortController();
		controllerRef.current = controller;

		setEnviando(true);
		setMensagemErro(null);

		try {
			if (!entrarUnificado) throw new Error("Autenticação indisponível.");
			const resultado = await entrarUnificado({
				payload: construirLoginUnificadoRequest({
					identificacao,
					usuario: dados.username,
					senha: dados.password,
					tokenTurnstile: token,
				}),
				identificacao,
				signal: controller.signal,
			});

			if (controller.signal.aborted || !montadoRef.current || !resultado) return null;
			if (resultado.status === 202)
				setDesafio({
					valor: resultado.dados.desafio_otp,
					expiraEm: Date.now() + resultado.dados.expiracao_segundos * 1000,
					identificacao,
					versaoSessao: resultado.versaoSessao,
				});
			return resultado.status;
		} catch (error: unknown) {
			if (controller.signal.aborted || !montadoRef.current) return null;
			bloqueio.registrarErro(error);
			setMensagemErro(obterMensagemErroLogin(error, grupo.origem === "codigo" ? "codigo" : "identificado"));
			return null;
		} finally {
			if (controllerRef.current === controller) controllerRef.current = null;
			if (montadoRef.current) setEnviando(false);
		}
	}

	function trocarGrupo(): void {
		if (enviando || desafio || entradaSpace) return;
		removerGrupoLembradoLogin();
		setGrupoLembrado(null);
		setMensagemErro(null);
		void navigate("/sign-in", { replace: true });
	}

	// -------------------------------- RETURN --------------------------------
	return {
		grupo,
		desafio,
		entradaSpace,
		abrirSpace,
		enviando,
		mensagemErro,
		bloqueio,
		entrar,
		trocarGrupo,
		cancelarDesafio,
		expirarDesafio,
	};
}
